/**
 * "The vendor already agreed to this price" (26 Oct). After the vendor agrees to RFI's counter, the negotiating agent
 * is asked again with the same vendor numbers and suggests the same prices. A line whose suggestion is exactly the
 * price RFI sent in its last counter gets a note, so Use Counter does not read like a new offer. Pure, so it is
 * unit-tested. When anything is missing or unclear it says nothing: a wrong note is worse than none.
 */

export const AGREED_PRICE_NOTE = "The vendor already agreed to this price. It's the counter you sent last round.";

/** Money within one cent counts as the same price. */
const SAME_PRICE_TOLERANCE = 0.015;

export interface AgreedPriceAgentLine {
  lineItemId: string;
  itemName?: string | null;
  vendorValue: number;
  suggestedValue: number;
}

export interface AgreedPriceRoundLine {
  itemName?: string | null;
  rowTotal?: number | null;
  isNotNeeded?: boolean | null;
}

export interface AgreedPriceRound {
  party: string;
  action: string;
  createdUtc?: string | null;
  lines?: ReadonlyArray<AgreedPriceRoundLine> | null;
}

function nameKey(name: string | null | undefined): string {
  return (name ?? '').trim().toLowerCase();
}

/** The latest round RFI sent as a counter (the one the vendor agreed to), by time when given, else by order. */
function lastRfiCounter(rounds: ReadonlyArray<AgreedPriceRound>): AgreedPriceRound | null {
  let best: AgreedPriceRound | null = null;
  let bestTime = -Infinity;
  rounds.forEach((round, index) => {
    if (round.party !== 'admin' || round.action !== 'counter') return;
    const t = round.createdUtc ? Date.parse(round.createdUtc) : NaN;
    const time = Number.isFinite(t) ? t : index;
    if (best === null || time >= bestTime) {
      best = round;
      bestTime = time;
    }
  });
  return best;
}

/**
 * Ids of the agent lines whose suggested price equals the price in RFI's last counter, for a vendor who agreed to it.
 * Skipped on purpose: lines the agent leaves at the vendor's own price (nothing was countered there), lines whose name
 * is not unique on either side, and lines without a matching counter line.
 */
export function agreedPriceLineIds(
  vendorAgreed: boolean,
  agentLines: ReadonlyArray<AgreedPriceAgentLine> | null | undefined,
  rounds: ReadonlyArray<AgreedPriceRound> | null | undefined,
): Set<string> {
  const ids = new Set<string>();
  if (!vendorAgreed || !agentLines?.length || !rounds?.length) return ids;
  const counter = lastRfiCounter(rounds);
  const counterLines = (counter?.lines ?? []).filter((l) => !l.isNotNeeded);
  if (!counter || counterLines.length === 0) return ids;

  for (const line of agentLines) {
    const key = nameKey(line.itemName);
    if (!key) continue;
    // Nothing was countered on a line the agent leaves at the vendor's price.
    if (Math.abs(line.suggestedValue - line.vendorValue) < SAME_PRICE_TOLERANCE) continue;
    const sameNameAgent = agentLines.filter((l) => nameKey(l.itemName) === key);
    const sameNameCounter = counterLines.filter((l) => nameKey(l.itemName) === key);
    if (sameNameAgent.length !== 1 || sameNameCounter.length !== 1) continue;
    const sent = sameNameCounter[0].rowTotal;
    if (sent == null || !Number.isFinite(sent)) continue;
    if (Math.abs(sent - line.suggestedValue) < SAME_PRICE_TOLERANCE) ids.add(line.lineItemId);
  }
  return ids;
}
